import React, { useEffect, useState } from 'react'
import {
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material'
import { com } from '@eidu/form'
import EntityType = com.eidu.sharedlib.entity.type.EntityType
import EntityTypeId = com.eidu.sharedlib.entity.type.EntityTypeId

type EntityTypeFieldProps = {
  entityTypeId?: EntityTypeId
  types: readonly EntityType[]
  onChange: (value: EntityType) => void
  label: string
  disabled?: boolean
}

function getSelectedType(
  entityTypeId: com.eidu.sharedlib.entity.type.EntityTypeId | undefined,
  types: readonly com.eidu.sharedlib.entity.type.EntityType[]
) {
  if (entityTypeId !== undefined) {
    return entityTypeId.toString()
  } else {
    return types.length > 0 ? types[0].id.toString() : ''
  }
}

const EntityTypeField: React.FC<EntityTypeFieldProps> = ({
  entityTypeId,
  types,
  onChange,
  label,
  disabled = false,
}) => {
  const [selectedOption, setSelectedOption] = useState<string>(getSelectedType(entityTypeId, types))
  const [hasError, setHasError] = useState<boolean>(false)

  useEffect(() => {
    const typeExists =
      entityTypeId === undefined || types.some((type) => type.id.toString() === entityTypeId.toString())
    setHasError(!typeExists)

    setSelectedOption(getSelectedType(entityTypeId, types))
  }, [entityTypeId, types])

  const handleChange = (event: SelectChangeEvent<string>) => {
    const { value } = event.target
    setSelectedOption(value)
    onChange(types.find((type) => type.id.toString() === value)!)
  }

  return (
    <FormControl disabled={disabled} error={hasError}>
      <InputLabel id="entity-type-label">{label}</InputLabel>
      <Select
        labelId="entity-type-label"
        label={label}
        value={selectedOption}
        onChange={handleChange}
        style={{ width: '300px' }}
      >
        {types.map((type) => (
          <MenuItem key={type.id.toString()} value={type.id.toString()}>
            <ListItemText primary={type.name} />
          </MenuItem>
        ))}
      </Select>
      {hasError && (
        <FormHelperText error>
          Warning: The selected entity type was not found in the available types. Please select a valid type.
        </FormHelperText>
      )}
    </FormControl>
  )
}

export default EntityTypeField
